import useSWR, { mutate as globalMutate } from 'swr';
import { fetcher } from '../../services/fetcher';
import api from '../../services/api';
import { moduleConfig } from './config';
import { useClients } from '../clients/useData';

export interface RecurrenceCompletion {
  todoId: number;
  occurrenceDate: string; // YYYY-MM-DD
  completedAt?: string | null;
  completedBy?: number | null;
  completedUserDisplayName?: string | null;
}

export interface Todo {
  todoId: number;
  clientId?: number;
  todoDescr?: string;
  todoCompleted: boolean;
  todoCompletedAt?: string;
  todoTypeId?: number;
  todoRecurring?: boolean;
  todoRecurrenceIntervalId?: number;
  todoRecurrenceDayOfMonth?: number;
  todoDueDate?: string;
  todoCreatedAt?: string;
  todoCreatedBy?: number;
  todoUpdatedAt?: string;
  todoUpdatedBy?: number;
  client?: {
    clientName?: string;
  };
  todoType?: {
    id: number;
    tt_descr: string;
    icon?: string | null;
  };
  todoRecurrenceInterval?: {
    id: number;
    tri_descr: string;
    months: number;
  };
  FileAttachment?: Array<{
    id: number;
    fileName?: string | null;
    filePath?: string | null;
    fileType?: string | null;
    fileSize?: number | null;
    uploadedAt?: string | null;
  }>;
}

export interface TodosResponse {
  data: Todo[];
  total: number;
  page: number;
  limit: number;
}

export const useTodos = (searchParams?: string) => {
  const queryString = searchParams ? `?searchParams=${encodeURIComponent(searchParams)}` : '';
  const { data, error, isLoading, mutate } = useSWR<Todo[]>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    todos: data || [], // Keep for backward compatibility
    total: data?.length || 0,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useTodo = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<Todo>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data: data,
    todo: data, // Keep for backward compatibility
    isLoading,
    isError: error,
    mutate,
  };
};

// Hook to get clients for select dropdown
export const useClientsForSelect = () => {
  const { clients, isLoading } = useClients();

  return {
    clients: clients || [],
    isLoading,
    options: (clients || []).map((client) => ({
      value: String(client.clientId),
      label: client.clientName || `Client #${client.clientId}`,
    })),
  };
};

/** Recurrence completions for a month (YYYY-MM). Used to show which recurring occurrences are done. */
export const useRecurrenceCompletions = (month: string | null) => {
  const key = month ? `${moduleConfig.module.api.list}/recurrence-completions?month=${month}` : null;
  const { data, error, isLoading, mutate } = useSWR<RecurrenceCompletion[]>(key, fetcher, {
    revalidateOnFocus: true,
  });
  return {
    completions: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const markRecurrenceComplete = async (todoId: number, occurrenceDate: string) => {
  await api.post(
    `${moduleConfig.module.api.detail(todoId)}/recurrence-completions?occurrenceDate=${encodeURIComponent(occurrenceDate)}`
  );
  globalMutate((k: string) => typeof k === 'string' && (k.startsWith('/crm-todos') || k.includes('recurrence-completions')));
};

export const markRecurrenceIncomplete = async (todoId: number, occurrenceDate: string) => {
  await api.delete(`${moduleConfig.module.api.detail(todoId)}/recurrence-completions/${occurrenceDate}`);
  globalMutate((k: string) => typeof k === 'string' && (k.startsWith('/crm-todos') || k.includes('recurrence-completions')));
};

// Hook to get todo recurrence intervals for select
export const useTodoRecurrenceIntervalsForSelect = () => {
  const { data, error, isLoading } = useSWR<Array<{ id: number; tri_descr: string; months: number }>>(
    '/p-crm-todo-recurrence-interval',
    fetcher,
    { revalidateOnFocus: false }
  );

  return {
    intervals: data || [],
    isLoading,
    isError: error,
    options: [
      { value: '', label: 'None' },
      ...(data || []).map((i) => ({ value: String(i.id), label: i.tri_descr })),
    ],
  };
};

// Hook to get todo types for select
export const useTodoTypesForSelect = () => {
  const { data, error, isLoading } = useSWR<Array<{ id: number; tt_descr: string; icon?: string | null }>>(
    '/p-crm-todo-type',
    fetcher,
    { revalidateOnFocus: false }
  );

  return {
    todoTypes: data || [],
    isLoading,
    isError: error,
    options: [
      { value: '', label: 'None' },
      ...(data || []).map((i) => ({ value: String(i.id), label: i.tt_descr })),
    ],
  };
};
